import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"
import { ensureFontLoaded } from "../fonts_loader";

export default class extends Controller {
    connect() {
        if (!this.choices) {
            this.choices = new Choices(this.element, {
                searchEnabled: false,
                itemSelectText: "",
                shouldSort: false,
            });
        }
        window.choicesRegistry ||= {};
        const key = this.element.dataset.choicesRole || this.element.id || `choices-${Date.now()}`;
        window.choicesRegistry[key] = this.choices;

        // --- NEW: preload visible items when dropdown opens, and on hover
        this._onShow = () => {
            this._warmVisibleFonts();
            this._bindHoverPreload();
        };
        this._onHide = () => this._unbindHoverPreload();

        // Choices emits these custom events on the original <select>
        this.element.addEventListener("showDropdown", this._onShow);
        this.element.addEventListener("hideDropdown", this._onHide);

        // sync from inspector
        this._onInspect = (e) => {
            const role = this.element.dataset.choicesRole
            if (!role) return
            let val = null
            if (role === "fontFamily") val = e.detail?.fontFamily
            if (role === "fontWeight") val = e.detail?.fontWeight
            if (role === "photoFilter") val = e.detail?.name
            if (val) {
                if (role === "fontFamily") {
                    ensureFontLoaded(val).finally(() => this._setValue(val));
                } else {
                    this._setValue(val);
                }
            }
        }
        document.addEventListener("ie:inspect", this._onInspect)
    }

    disconnect() {
        document.removeEventListener("ie:inspect", this._onInspect)
        this.element.removeEventListener("showDropdown", this._onShow);
        this.element.removeEventListener("hideDropdown", this._onHide);
        this._unbindHoverPreload();
    }

    handleSelectionChange() {
        const v = this.element.value
        this._setValue(v)

        const role = this.element.dataset.choicesRole
        if (role === "fontFamily") {
            Promise.resolve(ensureFontLoaded(v)).finally(() => {
                document.dispatchEvent(new CustomEvent("ie:setFontFamily", { detail: { family: v }}));
            });
            return;
        }
        if (role === "fontWeight") {
            document.dispatchEvent(new CustomEvent("ie:setFontWeight", { detail: { weight: v }}))
        }
        if (role === "photoFilter") {
            document.dispatchEvent(new CustomEvent("ie:photoFilter", { detail: { name: v }}))
        }
    }

    _setValue(v) {
        this.choices?.removeActiveItems();
        (this.choices?.setChoiceByValue?.(v) ?? this.choices?.setValueByChoice?.(v));
        this.element.value = v

        // --- NEW: force the visible "selected chip" to render in the chosen font
        const root = this.element.closest(".choices");
        const chip = root?.querySelector(".choices__list--single .choices__item");
        if (chip && v) chip.style.fontFamily = `"${v}", Arial, sans-serif`;
    }

    // --- NEW helpers ---
    _choicesRoot() {
        return this.element.closest(".choices");
    }

    _warmVisibleFonts() {
        const root = this._choicesRoot();
        const items = root?.querySelectorAll('.choices__list--dropdown .choices__item');
        if (!items) return;
        // Preload the ones currently rendered in the dropdown viewport
        items.forEach(it => {
            const fam = it.getAttribute("data-value");
            if (fam) ensureFontLoaded(fam);
        });
    }

    _bindHoverPreload() {
        if (this._hoverBound) return;
        const root = this._choicesRoot();
        const list = root?.querySelector(".choices__list--dropdown");
        if (!list) return;
        this._hoverBound = (e) => {
            const it = e.target.closest(".choices__item");
            if (!it) return;
            const fam = it.getAttribute("data-value");
            if (fam) ensureFontLoaded(fam);
        };
        list.addEventListener("mousemove", this._hoverBound);
        list.addEventListener("touchstart", this._hoverBound, { passive: true });
    }

    _unbindHoverPreload() {
        const root = this._choicesRoot();
        const list = root?.querySelector(".choices__list--dropdown");
        if (list && this._hoverBound) {
            list.removeEventListener("mousemove", this._hoverBound);
            list.removeEventListener("touchstart", this._hoverBound);
        }
        this._hoverBound = null;
    }

}